import axios from "axios";

// The Dev Board is decoupled from the platform's user auth. It keeps its own
// token + "who am I" actor label in localStorage, and its own axios instance so
// a dev-board 401 never triggers the app-wide login redirect.
const TOKEN_KEY = "devboard_token";
const ACTOR_KEY = "devboard_actor";

const devApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  headers: { "Content-Type": "application/json" },
});

devApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  const actor = localStorage.getItem(ACTOR_KEY);
  // HTTP headers must be latin1-safe; drop anything that isn't.
  if (actor && /^[\x20-\x7E]+$/.test(actor)) {
    config.headers["X-DevBoard-Actor"] = actor;
  }
  return config;
});

export const devBoardAuth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  isSignedIn: () => Boolean(localStorage.getItem(TOKEN_KEY)),
  setToken: (token) => localStorage.setItem(TOKEN_KEY, token),
  clearToken: () => localStorage.removeItem(TOKEN_KEY),
  getActor: () => localStorage.getItem(ACTOR_KEY) || "",
  setActor: (name) => {
    if (name) localStorage.setItem(ACTOR_KEY, name);
    else localStorage.removeItem(ACTOR_KEY);
  },
};

const unwrap = (p) => p.then((r) => r.data);

export const devBoardService = {
  getConfig: () => unwrap(devApi.get("/api/v1/dev-board/config")),
  authenticate: (passphrase) =>
    unwrap(devApi.post("/api/v1/dev-board/auth", { passphrase })),

  // Members (devs)
  getMembers: (includeInactive = false) =>
    unwrap(
      devApi.get("/api/v1/dev-board/members", {
        params: { include_inactive: includeInactive },
      })
    ),
  createMember: (data) => unwrap(devApi.post("/api/v1/dev-board/members", data)),
  updateMember: (id, data) =>
    unwrap(devApi.patch(`/api/v1/dev-board/members/${id}`, data)),
  deleteMember: (id) => unwrap(devApi.delete(`/api/v1/dev-board/members/${id}`)),

  // Labels
  getLabels: () => unwrap(devApi.get("/api/v1/dev-board/labels")),
  createLabel: (data) => unwrap(devApi.post("/api/v1/dev-board/labels", data)),
  updateLabel: (id, data) =>
    unwrap(devApi.patch(`/api/v1/dev-board/labels/${id}`, data)),
  deleteLabel: (id) => unwrap(devApi.delete(`/api/v1/dev-board/labels/${id}`)),

  // Tasks
  getTasks: (params = {}) =>
    unwrap(devApi.get("/api/v1/dev-board/tasks", { params })),
  getTask: (id) => unwrap(devApi.get(`/api/v1/dev-board/tasks/${id}`)),
  createTask: (data) => unwrap(devApi.post("/api/v1/dev-board/tasks", data)),
  updateTask: (id, data) =>
    unwrap(devApi.patch(`/api/v1/dev-board/tasks/${id}`, data)),
  deleteTask: (id) => unwrap(devApi.delete(`/api/v1/dev-board/tasks/${id}`)),

  // Comments
  addComment: (taskId, data) =>
    unwrap(devApi.post(`/api/v1/dev-board/tasks/${taskId}/comments`, data)),
};

export default devBoardService;
